<template>
  <div class="container mt-4">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4" style="color: #b03a45;">
        <i class="fas fa-university me-2"></i> {{ $t('bank.title') }}
      </h4>

      <!-- Form -->
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('bank.bankCode') }}</label>
              <input type="text" class="form-control" v-model="form.code" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('bank.bankName') }}</label>
              <input type="text" class="form-control" v-model="form.name" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded d-flex align-items-center justify-content-between">
              <label class="form-label mb-0">{{ $t('bank.status') }}</label>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" v-model="form.is_active" />
                <label class="form-check-label">
                  {{ form.is_active ? $t('bank.active') : $t('bank.inactive') }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex gap-3 mt-3">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            {{ editingId ? $t('bank.update') : $t('bank.add') }}
          </button>
          <button type="button" class="btn btn-secondary px-4" @click="resetForm">{{ $t('bank.cancel') }}</button>
        </div>
      </form>

      <!-- List -->
      <hr class="my-4" />
      <h5 class="mb-3" style="color: #b03a45;">
        <i class="fas fa-list me-2"></i> {{ $t('bank.existingBanks') }}
      </h5>

      <div v-if="banks.length" class="table-responsive">
        <table class="table table-bordered align-middle">
          <thead class="table-light">
            <tr>
              <th>{{ $t('bank.code') }}</th>
              <th>{{ $t('bank.name') }}</th>
              <th>{{ $t('bank.status') }}</th>
              <th class="text-center">{{ $t('bank.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="bank in banks" :key="bank.id">
              <td>{{ bank.code }}</td>
              <td>{{ bank.name }}</td>
              <td>
                <span :class="bank.is_active ? 'text-success' : 'text-danger'">
                  {{ bank.is_active ? $t('bank.active') : $t('bank.inactive') }}
                </span>
              </td>
              <td class="text-center">
                <button class="btn btn-outline-danger btn-sm me-2" @click="loadBank(bank)">{{ $t('bank.edit') }}</button>
                <button class="btn btn-outline-secondary btn-sm" @click="deleteBank(bank.id)">{{ $t('bank.delete') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="text-muted">{{ $t('bank.noBanks') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import bankService from '@/services/bankService'

const toast = useToast()
const banks = ref([])
const editingId = ref(null)

const form = ref({
  code: '',
  name: '',
  is_active: true
})

const fetchBanks = async () => {
  try {
    const response = await bankService.getAll()
    // Gère les deux formats: direct (production) et paginé (local)
    banks.value = response.data.data || response.data
  } catch (err) {
    toast.error($t('bank.failedToLoad'))
    console.error(err)
  }
}

const handleSubmit = async () => {
  try {
    if (editingId.value) {
      await bankService.update(editingId.value, form.value)
      toast.success($t('bank.bankUpdated'))
    } else {
      await bankService.create(form.value)
      toast.success($t('bank.bankAdded'))
    }
    resetForm()
    fetchBanks()
  } catch (err) {
    toast.error($t('bank.errorSaving'))
    console.error(err)
  }
}

const loadBank = (bank) => {
  editingId.value = bank.id
  form.value = {
    code: bank.code,
    name: bank.name,
    is_active: bank.is_active
  }
}

const resetForm = () => {
  editingId.value = null
  form.value = {
    code: '',
    name: '',
    is_active: true
  }
}

const deleteBank = async (id) => {
  if (!confirm($t('bank.confirmDelete'))) return
  try {
    await bankService.delete(id)
    toast.success($t('bank.bankDeleted'))
    fetchBanks()
  } catch (err) {
    toast.error($t('bank.failedToDelete'))
    console.error(err)
  }
}

onMounted(() => {
  fetchBanks()
})
</script>
